import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';

import {
  BlogRoot,
  Card,
  CardCategory,
  CardCover,
  CardDescription,
  CardMeta,
  CardTitle,
  CategoryHeader,
  CategoryNav,
  CategoryPill,
  Container,
  Empty,
  Grid,
  Page,
} from '../../../components/blog/Blog.styles';
import JsonLd from '../../../components/blog/JsonLd';
import Layout from '../../../components/layout/Layout';
import {
  getPostsByCategoryMeta,
  getUsedCategories,
} from '../../../modules/blog';
import {
  categoryToSlug,
  slugToCategory,
} from '../../../modules/blogCategories';
import { SITE_NAME, SITE_URL } from '../../../modules/blogConstants';
import { formatDate } from '../../../modules/blogUtils';
import { userBasicInfoPublicPages } from '../../../modules/graphqlQueries';
import { useLoadData } from '../../../modules/useLoadData';
import { PostMetaType } from '../../../types/BlogTypes';
import { UserType } from '../../../types/UserTypes';

interface CategoryPageProps {
  category: string;
  posts: PostMetaType[];
  categories: string[];
}

const getQuery = (userId: string) => `{
  user(_id: "${userId}") {
    ${userBasicInfoPublicPages}
  }
}`;

const CategoryPage = ({
  category,
  posts,
  categories,
}: CategoryPageProps): JSX.Element => {
  const {
    isLoading,
    response: {
      data: { user },
    },
  } = useLoadData(getQuery);

  const canonical = `${SITE_URL}/blog/category/${categoryToSlug(category)}`;
  const pageTitle = `${category} Articles | ${SITE_NAME}`;
  const pageDescription = `Browse all ${category.toLowerCase()} articles on ${SITE_NAME}.`;

  return (
    <Layout user={user as UserType} isLoading={false} isUserLoading={isLoading}>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="robots"
          content="index,follow,max-image-preview:large,max-snippet:-1"
        />
        <link rel="canonical" href={canonical} />
        <link
          rel="alternate"
          type="application/rss+xml"
          title={`${SITE_NAME} Blog RSS`}
          href={`${SITE_URL}/rss.xml`}
        />
        <meta property="og:title" content={pageTitle} key="og:title" />
        <meta
          property="og:description"
          content={pageDescription}
          key="og:description"
        />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" key="og:type" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin="anonymous"
        />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght,SOFT@0,9..144,300..700,0..100;1,9..144,300..700,0..100&family=DM+Sans:ital,opsz,wght@0,9..40,300..700;1,9..40,300..700&display=swap"
        />
      </Head>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: pageTitle,
          description: pageDescription,
          url: canonical,
          inLanguage: 'en-US',
          isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: SITE_URL },
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: posts.map((p, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              url: `${SITE_URL}/blog/${p.slug}`,
              name: p.title,
            })),
          },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: `${SITE_URL}/blog`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: category,
              item: canonical,
            },
          ],
        }}
      />

      <BlogRoot>
        <CategoryHeader>
          <Container>
            <span className="overline">Dispatch / {category}</span>
            <h1>{category}</h1>
            <p>
              {posts.length} article{posts.length !== 1 ? 's' : ''}
            </p>
          </Container>
        </CategoryHeader>

        <Container>
          <CategoryNav>
            <Link href="/blog" passHref legacyBehavior>
              <CategoryPill>All</CategoryPill>
            </Link>
            {categories.map((c) => (
              <Link
                key={c}
                href={`/blog/category/${categoryToSlug(c)}`}
                passHref
                legacyBehavior
              >
                <CategoryPill active={c === category}>{c}</CategoryPill>
              </Link>
            ))}
          </CategoryNav>
        </Container>

        <Container>
          <Page>
            {posts.length ? (
              <Grid>
                {posts.map((post) => (
                  <Link
                    key={post.slug}
                    href={`/blog/${post.slug}`}
                    passHref
                    legacyBehavior
                  >
                    <Card>
                      <CardCover>
                        {post.coverImage ? (
                          <img
                            src={post.coverImage}
                            alt={post.title}
                            loading="lazy"
                          />
                        ) : (
                          <span className="fallback">B2B</span>
                        )}
                      </CardCover>
                      <CardCategory>{post.category}</CardCategory>
                      <CardTitle>{post.title}</CardTitle>
                      <CardDescription>{post.description}</CardDescription>
                      <CardMeta>
                        <time dateTime={post.date}>
                          {formatDate(post.date)}
                        </time>
                        <span className="dot" />
                        <span>{post.readingTime}</span>
                      </CardMeta>
                    </Card>
                  </Link>
                ))}
              </Grid>
            ) : (
              <Empty>No dispatches in this category yet.</Empty>
            )}
          </Page>
        </Container>
      </BlogRoot>
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: getUsedCategories().map((c) => ({
      params: { slug: categoryToSlug(c) },
    })),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<CategoryPageProps> = async ({
  params,
}) => {
  const slug = params?.slug as string;
  const category = slugToCategory(slug);
  if (!category) {
    return { notFound: true };
  }
  return {
    props: {
      category,
      posts: getPostsByCategoryMeta(category),
      categories: getUsedCategories(),
    },
  };
};

export default CategoryPage;
